import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'

import type { GA4SearchRedirectErrorDisplay } from '@/ga4types'
import useBootstrapData from '@/hooks/useBootstrapData'
import analytics from '@/shared-utils/analytics'
import { consumePkgSessionRecoveryForLandingForm } from '@/shared-utils/pkg-session-recovery'

import { initialState } from '../SearchForm/utils'
import type { PackagesFormStateType } from '../types'
import usePkgDefaultHpSearchPassengers from '../usePkgDefaultHpSearchPassengers'

type UsePackagesFormInitialValuesParams = {
  pageName: 'landing' | 'homepage'
}

const usePackagesFormInitialValues = ({ pageName }: UsePackagesFormInitialValuesParams) => {
  const {
    isMobile,
    prePopData,
    seti,
    webstats: { clientGUID: cguid },
    locale: { localeCode = 'en-US' } = {},
  } = useBootstrapData()
  const { query } = useRouter()

  const {
    tripType,
    startDate: prePopStartDate,
    endDate: prePopEndDate,
    startLocation,
    endLocation,
    adultCount,
    childrenCount,
    infantCount,
    roomCount,
  } = prePopData?.packages ?? {}

  const isErrorRedirect =
    pageName === 'landing' &&
    typeof query['redirect-source'] === 'string' &&
    query['redirect-source'].toLowerCase() === 'error'

  // Lazy init; session payload is left in place so Strict Mode remounts can re-read it.
  const [sessionRecovery] = useState(() =>
    isErrorRedirect
      ? consumePkgSessionRecoveryForLandingForm({ seti })
      : { formState: null, restored: false },
  )

  // Recovered travelers replace the experiment default, so those users must stay out of the test.
  const defaultAdults = usePkgDefaultHpSearchPassengers(
    adultCount || sessionRecovery.formState?.travelers?.adults,
  )

  useEffect(() => {
    if (!sessionRecovery.restored) {
      return
    }
    const ga4Event: GA4SearchRedirectErrorDisplay = {
      event: 'display',
      attributes: {
        type: 'pkg_landing_search_redirect_error',
        item_name: 'search_data_persisted',
        page_name: 'landing_page',
        product_name: 'bundle',
      },
    }
    analytics.fireGA4Event(ga4Event)
  }, [sessionRecovery.restored])

  const initialValues: PackagesFormStateType = useMemo(() => {
    const prePopValues: PackagesFormStateType = {
      ...initialState,
      ...(tripType && { tripType }),
      ...(prePopStartDate && { startDate: prePopStartDate }),
      ...(prePopEndDate && { endDate: prePopEndDate }),
      ...(startLocation && { startLocation }),
      ...(endLocation && { endLocation }),
      travelers: {
        adults: defaultAdults,
        children: childrenCount || 0,
        lapInfants: infantCount || 0,
        childrenAges: {},
        rooms: roomCount || 1,
      },
    }

    if (!sessionRecovery.formState) {
      return prePopValues
    }

    return {
      ...prePopValues,
      ...sessionRecovery.formState,
      travelers: {
        ...prePopValues.travelers,
        ...sessionRecovery.formState.travelers,
      },
    }
  }, [
    tripType,
    prePopStartDate,
    prePopEndDate,
    startLocation,
    endLocation,
    defaultAdults,
    childrenCount,
    infantCount,
    roomCount,
    sessionRecovery.formState,
  ])

  return {
    initialValues,
    isMobile,
    cguid,
    localeCode,
  }
}

export default usePackagesFormInitialValues
